import React, { useEffect, useState } from "react";
import { Trash2, MailCheck, CheckCircle2, Circle, Reply } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Header } from "@/pages/admin/AdminDashboard";

export default function AdminSubmissions() {
    const [items, setItems] = useState([]);
    const [active, setActive] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        const res = await api.get("/admin/submissions");
        setItems(res.data);
        setLoading(false);
        if (res.data.length && !active) setActive(res.data[0]);
    };
    useEffect(() => { load(); }, []);

    const toggleRead = async (s, value) => {
        await api.patch(`/admin/submissions/${s.id}`, { read: value });
        const next = items.map((x) => (x.id === s.id ? { ...x, read: value } : x));
        setItems(next);
        if (active?.id === s.id) setActive({ ...active, read: value });
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this submission?")) return;
        await api.delete(`/admin/submissions/${id}`);
        toast.success("Submission deleted");
        const next = items.filter((x) => x.id !== id);
        setItems(next);
        setActive(next[0] || null);
    };

    return (
        <div data-testid="admin-submissions-page">
            <Header
                title="Submissions"
                subtitle="Pesan masuk dari form kontak. Email otomatis terkirim ke contact@akhmadferdian.com via Resend."
            />

            <div className="grid lg:grid-cols-[380px_1fr] gap-6 h-[calc(100vh-220px)]">
                <aside className="rounded-xl border border-white/5 bg-ink-800/40 overflow-y-auto">
                    {loading ? (
                        <div className="p-6 mono-label">Loading…</div>
                    ) : items.length === 0 ? (
                        <div className="p-6 text-ink-300">No submissions yet.</div>
                    ) : (
                        <ul className="divide-y divide-white/5">
                            {items.map((s) => (
                                <li
                                    key={s.id}
                                    onClick={() => {
                                        setActive(s);
                                        if (!s.read) toggleRead(s, true);
                                    }}
                                    data-testid={`submission-item-${s.id}`}
                                    className={`p-4 cursor-pointer transition-colors ${
                                        active?.id === s.id ? "bg-white/5" : "hover:bg-white/5"
                                    }`}
                                >
                                    <div className="flex items-center gap-2 mb-1">
                                        {s.read ? <CheckCircle2 size={12} className="text-ink-300" /> : <Circle size={12} className="text-brand fill-brand" />}
                                        <span className="text-white text-sm font-medium truncate">{s.name}</span>
                                        <span className="ml-auto text-[10px] text-ink-300 font-mono">{new Date(s.created_at).toLocaleDateString()}</span>
                                    </div>
                                    <div className="text-xs text-ink-300 font-mono truncate">{s.email}</div>
                                    <div className="mt-1 text-sm text-ink-100 truncate">{s.subject || s.message}</div>
                                </li>
                            ))}
                        </ul>
                    )}
                </aside>

                <section className="rounded-xl border border-white/5 bg-ink-800/40 overflow-y-auto">
                    {!active ? (
                        <div className="h-full flex items-center justify-center text-ink-300">Select a submission</div>
                    ) : (
                        <div className="p-6 lg:p-8">
                            <div className="flex flex-wrap items-start justify-between gap-3 mb-6 border-b border-white/5 pb-6">
                                <div>
                                    <h2 className="font-display text-2xl text-white">{active.subject || "(no subject)"}</h2>
                                    <div className="mt-2 text-sm text-ink-200">
                                        From <span className="text-white">{active.name}</span> · <a href={`mailto:${active.email}`} className="text-brand">{active.email}</a>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    {active.email_sent && (
                                        <span className="inline-flex items-center gap-2 text-xs font-mono px-3 py-1.5 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-300">
                                            <MailCheck size={12} /> Email sent
                                        </span>
                                    )}
                                    <a
                                        href={`mailto:${active.email}?subject=Re: ${active.subject || ""}`}
                                        className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand text-white hover:bg-brand-hover text-sm"
                                    >
                                        <Reply size={14} /> Reply
                                    </a>
                                    <button
                                        onClick={() => toggleRead(active, !active.read)}
                                        className="px-4 py-2 rounded-md border border-white/10 text-ink-100 hover:bg-white/5 text-sm"
                                    >
                                        Mark as {active.read ? "unread" : "read"}
                                    </button>
                                    <button
                                        onClick={() => remove(active.id)}
                                        data-testid="submission-delete"
                                        className="p-2 rounded-md border border-white/10 text-ink-200 hover:bg-red-500/10 hover:text-red-400"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>

                            <div className="grid sm:grid-cols-3 gap-4 mb-6">
                                <Meta label="Company" value={active.company || "—"} />
                                <Meta label="Budget" value={active.budget || "—"} />
                                <Meta label="Received" value={new Date(active.created_at).toLocaleString()} />
                            </div>

                            <div className="p-5 rounded-lg border border-white/5 bg-ink-900/50 whitespace-pre-wrap text-ink-100 leading-relaxed">
                                {active.message}
                            </div>
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
}

function Meta({ label, value }) {
    return (
        <div className="p-3 rounded-lg border border-white/5 bg-ink-900/40">
            <div className="mono-label">{label}</div>
            <div className="text-white text-sm mt-1 truncate">{value}</div>
        </div>
    );
}
